<template lang="pug">
aside
  LabelledValue(label="Issue" :value="page.issue.name")
  LabelledValue(label="Slug" :value="page.slug")
  LabelledEditableValue(label="Notes" v-model="notes" :multi-line="true" @blur="save")
  
  .preview-section(v-if="page.preview_image_url || page.issue")
    h2 Page Preview
    .preview-thumbnail(v-if="page.preview_image_url" @click="showImageModal")
      img(:src="page.preview_image_url" alt="Page preview" width="100%" style="cursor: pointer;" title="Click to view full size")
    .preview-actions(v-if="page.preview_image_url")
      button.btn.btn-sm.btn-primary(@click="openThumbnailSelector")
        i.fa.fa-image
        |  Change Preview
      button.btn.btn-sm.btn-secondary.ms-2(@click="refreshPreview" :disabled="isRefreshing")
        i.fa.fa-sync(:class="{ 'fa-spin': isRefreshing }")
        |  {{ isRefreshing ? 'Refreshing...' : 'Refresh' }}
    .no-preview(v-else)
      p.text-muted No preview selected
      .preview-actions
        button.btn.btn-primary(@click="openThumbnailSelector" v-if="hasAdobePreviews")
          i.fa.fa-image
          |  Select Preview
        p.text-muted(v-else)
          | Add an Adobe preview URL on the 
          router-link(:to="`/issues/${page.issue.id}`") issue page
          |  to generate previews.
  
  PreviewThumbnailSelectorModal(
    ref="thumbnailSelector"
    :page="page"
    @save="handleThumbnailSave"
  )

  .modal.fade(ref="imageModal" tabindex="-1" aria-hidden="true")
    .modal-dialog.modal-lg.modal-dialog-centered
      .modal-content
        .modal-header
          h5.modal-title Preview Image
          button.close(type="button" data-dismiss="modal" aria-label="Close")
            span(aria-hidden="true") &times;
        .modal-body.text-center
          img(:src="page.preview_image_url" alt="Page preview" style="max-width: 100%; height: auto;")
</template>

<script>
  import { mapStores } from 'pinia'
  import { usePageStore } from '@/stores/page'
  import httpClient from '@/api/httpClient'
  import LabelledValue from '@/components/shared/labelled_value.vue'
  import LabelledEditableValue from '@/components/shared/labelled_editable_value.vue'
  import PreviewThumbnailSelectorModal from './preview_thumbnail_selector_modal.vue'

  export default {
    components: {
      LabelledValue,
      LabelledEditableValue,
      PreviewThumbnailSelectorModal
    },

    data() {
      return {
        isRefreshing: false,
        pollInterval: null
      }
    },

    computed: {
      ...mapStores(usePageStore),
      page() { return this.pageStore.page },
      notes: {
        get() { return this.pageStore.page.notes },
        set(v) { this.pageStore.page.notes = v }
      },
      hasAdobePreviews() {
        // Check if issue has any adobe previews
        return this.page.issue && this.page.issue.adobe_previews && this.page.issue.adobe_previews.length > 0
      }
    },

    beforeUnmount() {
      this.stopPolling()
    },

    methods: {
      showImageModal() {
        jQuery(this.$refs.imageModal).modal('show')
      },

      save() {
        this.pageStore.update()
      },

      openThumbnailSelector() {
        this.$refs.thumbnailSelector.show()
      },

      async handleThumbnailSave(data) {
        try {
          // Update the backend with both filename and adobe_preview_id
          await httpClient.patch(`/api/internal/pages/${this.page.id}`, {
            page: { 
              preview_image_filename: data.preview_image_filename,
              adobe_preview_id: data.adobe_preview_id
            }
          })
          
          // Fetch fresh page data from the server
          const response = await httpClient.get(`/api/internal/pages/${this.page.id}`)
          
          // Force Vue to update by replacing the entire page object
          this.pageStore.$patch({
            page: response.data.page
          })
          
          this.$store.dispatch('messages/addNotice', 'Preview thumbnail updated')
        } catch (error) {
          console.error('Failed to update preview thumbnail:', error)
          this.$store.dispatch('messages/addError', error)
        }
      },

      async refreshPreview() {
        // Only refresh if page has an adobe_preview_id
        if (!this.page.adobe_preview_id) {
          this.$store.dispatch('messages/addError', new Error('No preview selected'))
          return
        }
        
        this.isRefreshing = true
        try {
          // Check if there's a new version available for this specific preview
          await httpClient.post(`/api/internal/adobe_previews/${this.page.adobe_preview_id}/check_version`)
          
          // Fetch updated preview data
          const previewResponse = await httpClient.get(`/api/internal/adobe_previews/${this.page.adobe_preview_id}`)
          const preview = previewResponse.data.adobe_preview
          
          // Check for metadata fetch errors
          if (preview.fetch_status === 'metadata_error') {
            this.$store.dispatch('messages/addError', new Error(preview.fetch_message || 'Unable to check current version. Please try again in a few minutes.'))
            this.isRefreshing = false
            return
          }
          
          // If update available, fetch the new preview
          if (preview.update_available) {
            this.$store.dispatch('messages/addNotice', 'New version found! Fetching updated preview...')
            await httpClient.post(`/api/internal/adobe_previews/${this.page.adobe_preview_id}/fetch`)
            
            // Start polling for completion
            this.startPolling()
          } else {
            this.$store.dispatch('messages/addNotice', 'Preview is already up to date')
            this.isRefreshing = false
          }
          
        } catch (error) {
          console.error('Failed to refresh preview:', error)
          this.$store.dispatch('messages/addError', error)
          this.isRefreshing = false
        }
      },

      startPolling() {
        let attempts = 0
        const maxAttempts = 240 // 4 minutes total

        this.pollInterval = setInterval(async () => {
          attempts++
          
          // Fetch updated preview data
          const previewResponse = await httpClient.get(`/api/internal/adobe_previews/${this.page.adobe_preview_id}`)
          const preview = previewResponse.data.adobe_preview
          
          // Check if fetch completed successfully
          const isComplete = preview.fetch_status === 'complete'
          const hasError = preview.fetch_status === 'error'
          
          if (isComplete) {
            this.stopPolling()
            
            // Reload page data to get the updated preview with cache-busting
            const response = await httpClient.get(`/api/internal/pages/${this.page.id}`)
            const updatedPage = response.data.page
            
            // Add cache-busting timestamp to force image reload
            if (updatedPage.preview_image_url) {
              updatedPage.preview_image_url = `${updatedPage.preview_image_url.split('?')[0]}?t=${Date.now()}`
            }
            
            this.pageStore.$patch({
              page: updatedPage
            })
            
            this.isRefreshing = false
            this.$store.dispatch('messages/addNotice', 'Preview refreshed successfully')
            return
          }
          
          if (hasError) {
            this.stopPolling()
            this.isRefreshing = false
            const errorMsg = preview.fetch_message || 'Unknown error'
            this.$store.dispatch('messages/addError', new Error(`Preview fetch failed: ${errorMsg}`))
            return
          }

          // Timeout after max attempts
          if (attempts >= maxAttempts) {
            this.stopPolling()
            this.isRefreshing = false
            this.$store.dispatch('messages/addError', new Error('Preview fetch is taking longer than expected'))
          }
        }, 1000) // Poll every 1 second
      },

      stopPolling() {
        if (this.pollInterval) {
          clearInterval(this.pollInterval)
          this.pollInterval = null
        }
      }
    }
  }
</script>

<style scoped>
  .preview-section {
    margin-top: 30px;
  }


  .preview-thumbnail {
    max-width: 100%;
    margin-bottom: 0.75rem;
  }

  .preview-thumbnail img {
    width: 100%;
    height: auto;
    display: block;
    border: 1px solid #b1b0b0;
  }

  .preview-actions {
    text-align: center;
  }

  .no-preview {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 4px;
    text-align: center;
  }

  .no-preview p {
    margin-bottom: 0.5rem;
  }

  .no-preview small {
    display: block;
    font-size: 0.85rem;
  }
</style>
